/**
 * Prefetch service for background data loading
 * 
 * Loads data in the background when the app starts so pages load instantly
 */

import { Template } from '../types';
import { apiService } from './api';

// Simple in-memory cache for prefetched data
interface PrefetchCache {
  templates: Template[] | null;
  templatesLoading: boolean;
  templatesError: Error | null;
  templatesLoadedAt: number | null;
}

const cache: PrefetchCache = {
  templates: null,
  templatesLoading: false,
  templatesError: null,
  templatesLoadedAt: null,
};

// Cache expiry time (5 minutes)
const CACHE_TTL = 5 * 60 * 1000;

/**
 * Check if cached data is still valid
 */
function isCacheValid(loadedAt: number | null): boolean {
  if (!loadedAt) return false;
  return Date.now() - loadedAt < CACHE_TTL;
}

/**
 * Prefetch templates in the background
 */
export async function prefetchTemplates(): Promise<void> {
  // Skip if already loading or cache is valid
  if (cache.templatesLoading || isCacheValid(cache.templatesLoadedAt)) {
    return;
  }

  cache.templatesLoading = true;
  cache.templatesError = null;

  try {
    const templates = await apiService.getTemplates();
    cache.templates = templates;
    cache.templatesLoadedAt = Date.now();
  } catch (err) {
    cache.templatesError = err instanceof Error ? err : new Error('Failed to prefetch templates');
    console.error('Failed to prefetch templates:', err);
  } finally {
    cache.templatesLoading = false;
  }
}

/**
 * Get prefetched templates (returns null if not yet loaded)
 */
export function getPrefetchedTemplates(): Template[] | null {
  if (isCacheValid(cache.templatesLoadedAt)) {
    return cache.templates;
  }
  return null;
}

/**
 * Check if templates are currently being prefetched
 */
export function isTemplatesLoading(): boolean {
  return cache.templatesLoading;
}

/**
 * Invalidate templates cache (call after create/update/delete)
 */
export function invalidateTemplatesCache(): void {
  cache.templates = null;
  cache.templatesLoadedAt = null;
}

/**
 * Update templates cache with new data
 */
export function updateTemplatesCache(templates: Template[]): void {
  cache.templates = templates;
  cache.templatesLoadedAt = Date.now();
}

/**
 * Start all background prefetching
 * Call this when the app initializes
 */
export function startPrefetching(): void {
  // Prefetch templates in the background
  prefetchTemplates();
}
